import { Component, computed, signal, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from '../../../core/service/auth/auth.service';
import { PermissionService } from '../../../core/service/permission/permission.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface NavItem {
  label: string;
  route: string;
  icon: string;
  // Set this to hide the entry unless the user holds the permission.
  permission?: { project: string; module: string; action: string };
}

// Finance modules get appended here as they are added.
const FEDERATION_NAV: NavItem[] = [
  { label: 'Tableau de bord', route: '/federation/dashboard', icon: 'dashboard' },
  {
    label: 'Identité fédération', route: '/federation/identite', icon: 'account_balance',
    permission: { project: 'finances', module: 'identity', action: 'read' },
  },
  {
    label: 'Barème', route: '/federation/bareme', icon: 'price_change',
    permission: { project: 'finances', module: 'bareme', action: 'read' },
  },
  {
    label: 'Comptes clubs', route: '/federation/clubs', icon: 'groups',
    permission: { project: 'finances', module: 'clubs', action: 'read' },
  },
  {
    // « club » explicite : les encaissements hors club (subvention, sponsoring…) ont
    // leur écran distinct juste après, et le libellé nu ne dirait plus de quoi il parle.
    label: 'Encaissement club', route: '/federation/encaissements', icon: 'receipt_long',
    permission: { project: 'finances', module: 'encaissements', action: 'read' },
  },
  {
    // L'argent qui entre sans venir d'un club : subvention, sponsoring, don,
    // billetterie, remboursement reçu. Placé juste après pour que les deux écrans
    // d'encaissement se lisent ensemble.
    label: 'Autres encaissements', route: '/federation/autres-encaissements', icon: 'volunteer_activism',
    permission: { project: 'finances', module: 'autres_encaissements', action: 'read' },
  },
  {
    // « Paiements » ne disait pas à quelle étape on est : cet écran porte les
    // DÉCLARATIONS des clubs, à vérifier ou déjà traitées. Le journal de caisse,
    // c'est « Encaissement club ».
    label: 'Déclaration du club', route: '/federation/paiements', icon: 'description',
    permission: { project: 'finances', module: 'factures', action: 'read' },
  },
  {
    // §7 Ordres de paiement + §8 Décaissement : un seul écran, trois onglets.
    label: 'Décaissements', route: '/federation/decaissements', icon: 'call_made',
    permission: { project: 'finances', module: 'ordres', action: 'read' },
  },
  {
    label: 'Trésorerie', route: '/federation/tresorerie', icon: 'savings',
    permission: { project: 'finances', module: 'tresorerie', action: 'read' },
  },
  {
    label: 'Relances', route: '/federation/relances', icon: 'campaign',
    permission: { project: 'finances', module: 'relances', action: 'read' },
  },
  {
    label: 'Blocage', route: '/federation/blocage', icon: 'block',
    permission: { project: 'finances', module: 'blocage', action: 'read' },
  },
];

const CLUB_NAV: NavItem[] = [
  { label: 'Tableau de bord', route: '/club/dashboard', icon: 'dashboard' },
  {
    label: 'Situation financière', route: '/club/situation', icon: 'account_balance_wallet',
    permission: { project: 'finances', module: 'situation', action: 'read' },
  },
  {
    label: 'Payer', route: '/club/paiement', icon: 'payments',
    // Déclarer un paiement engage le club : un compte en lecture seule ne doit pas voir
    // cette entrée, et c'est la permission que le POST vérifie côté serveur.
    permission: { project: 'finances', module: 'situation', action: 'declarer' },
  },
  {
    label: 'Paiements', route: '/club/paiements', icon: 'description',
    permission: { project: 'finances', module: 'situation', action: 'read' },
  },
];

@Component({
  selector: 'app-sidebar',
  standalone: false,
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnDestroy {
  // Tablet (768-1023px): start on the icon rail so the 256px menu does not eat half the screen.
  isOpen = signal(typeof window === 'undefined' || window.innerWidth < 768 || window.innerWidth >= 1024);
  mobileOpen = signal(false);
  currentUrl = signal<string>('');

  private destroy$ = new Subject<void>();

  navItems = computed<NavItem[]>(() => {
    // Trois cas, pas deux : un compte qui n'est ni club ni fédération n'a AUCUN menu.
    // Le retomber sur le menu fédération lui montrait « Tableau de bord » — la seule
    // entrée sans permission — vers une route que la garde d'espace refuse désormais.
    const items = this.authService.isClub() ? CLUB_NAV
      : this.authService.isFederation() ? FEDERATION_NAV
      : [];
    return items.filter(item =>
      !item.permission || this.permService.can(item.permission.project, item.permission.module, item.permission.action)
    );
  });

  platformLabel = computed<string>(() => this.authService.isClub() ? 'Club' : 'Fédération');

  constructor(
    private router: Router,
    private authService: AuthService,
    private permService: PermissionService,
  ) {
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e: any) => this.currentUrl.set(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar(): void { this.isOpen.update(v => !v); }
  toggleMobile(): void {
    this.mobileOpen.update(v => !v);
    // The phone drawer always shows labels, never the icon rail.
    if (this.mobileOpen() && !this.isOpen()) this.isOpen.set(true);
  }
  closeMobile(): void { this.mobileOpen.set(false); }

  isActive(route: string): boolean {
    const url = this.currentUrl();
    return url === route || url.startsWith(route + '/');
  }

  navigate(route: string): void {
    this.closeMobile();
    this.router.navigate([route]);
  }
}
